import { Auth } from 'aws-amplify'
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'

import { CacheAdapter } from '@/shared/infra'

type CurrentUser = {
  Session: null
  attributes: {
    email: string
    email_verified: boolean
    family_name: string
    given_name: string
    locale: string
    name: string
    phone_number: string
    phone_number_verified: true
    sub: string
  }
  authenticationFlowType: 'USER_SRP_AUTH'
  signInUserSession: {
    idToken: {
      jwtToken: string
      payload: {
        aud: string
        auth_time: number
        authorities: string
        'cognito:username': 'e2638189-900d-45e9-80a0-b22de62de3c9'
        companyRelationTypes: string
        email: string
        email_verified: boolean
        event_id: string
        exp: number
        family_name: string
        given_name: string
        iat: number
        iss: string
        locale: string
        name: string
        phone_number: string
        phone_number_verified: boolean
        sub: string
        token_use: 'id'
      }
    }
  }
}

interface AuthContextProps {
  user: CurrentUser | null
}

export const AuthContext = createContext<AuthContextProps>(
  {} as AuthContextProps,
)

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<CurrentUser | null>(null)

  useEffect(() => {
    const userData = CacheAdapter.get('userData')

    if (Object.keys(userData).length > 0) {
      setUser(userData)
    } else {
      Auth.currentAuthenticatedUser()
        .then((user) => {
          CacheAdapter.set('userData', user)
          localStorage.removeItem('loginRequired')
          setUser(user)
        })
        .catch((err) => {
          console.log(err)
        })
    }
  }, [])

  return (
    <AuthContext.Provider value={{ user }}>{children}</AuthContext.Provider>
  )
}

export const useAuthContext = () => {
  const context = useContext(AuthContext)

  const [permissions, setPermissions] = useState({
    isCustomerGroupAdmin: false,
    isAgencyOfficeAdmin: false,
    isAppUser: false,
    isSystemAdmin: false,
    isBatchBooker: false,
    isChainAgencyAdmin: false,
    isCustomerAdmin: false,
    isAgencyAdmin: false,
  })

  const { user } = context

  const userAuthorities: Record<string, string[]> =
    Object.keys(user ?? {}).length > 0
      ? JSON.parse(user!.signInUserSession.idToken.payload.authorities)
      : {}

  const roles: string[] = userAuthorities.BOOKING_API ?? []

  useEffect(() => {
    const userAuthorities: Record<string, string[]> =
      Object.keys(user ?? {}).length > 0
        ? JSON.parse(user!.signInUserSession.idToken.payload.authorities)
        : {}

    const hasAuthorities = Object.keys(userAuthorities).length > 0

    setPermissions({
      isAgencyAdmin: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('AGENCY_ADMIN')
        : false,
      isAgencyOfficeAdmin: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('AGENCY_OFFICE_ADMIN')
        : false,
      isAppUser: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('APP_USER')
        : false,
      isBatchBooker: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('BATCH_BOOKER')
        : false,
      isChainAgencyAdmin: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('CHAIN_AGENCY_ADMIN')
        : false,
      isCustomerAdmin: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('CUSTOMER_ADMIN')
        : false,
      isCustomerGroupAdmin: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('CUSTOMER_GROUP_ADMIN')
        : false,
      isSystemAdmin: hasAuthorities
        ? userAuthorities.BOOKING_API?.includes('SYS_ADMIN')
        : false,
    })
  }, [user])

  return { ...context, roles, ...permissions }
}
